import { useCallback, useState } from "react";
import { usePermissions } from "../../Context/permissionContext";
import { DocType } from "../../components-v2/SearchClientForm";
import { notifyError, notifySucces } from "../../utils/notifications";
import { isCustomError } from "../../utils/validations";
import { LaunchAdjustmentFormSchema } from "../../components-v2/LaunchAdjustmentForm/schema";
import api from "../../services/api";

type Client = {
  id: number;
  name: string;
  statusCliente: string;
};

export function useLaunchAdjustmentPage() {
  const { permissions } = usePermissions();

  const [formSteps, setFormSteps] = useState(0);
  const [currentDoc, setCurrentDoc] = useState<DocType>("cpf");
  const [loadingBtn, setLoadingBtn] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [typesAdjustments, setTypesAdjustments] = useState<
    { id: string; descricao: string }[]
  >([]);
  const [selectedClient, setSelectedClient] = useState<null | Client>(null);

  const hasPermissionToUsePreviousDate = permissions.includes(
    "permission_launch_adjustment_with_previous_date"
  );

  const currentMask =
    currentDoc === "cpf" ? "999.999.999-99" : "99999999999999999";

  const handleTab = (doc: DocType) => {
    setCurrentDoc(doc);
    setClients([]);
  };

  const handleResetSearch = () => {
    setFormSteps(0);
    setClients([]);
    setSelectedClient(null);
  };

  const getData = useCallback(async () => {
    try {
      setLoadingTypes(true);
      const response = await api.get("get_types_adjustments_launch");

      const hasCustomError = isCustomError(response);

      if (hasCustomError) {
        return notifyError(response.message, "", false, true);
      }

      setTypesAdjustments(response.data);
    } catch (e) {
      const error = e as ErrorMessage;
      console.error(error.message);
    } finally {
      setLoadingTypes(false);
    }
  }, []);

  const onSelectClient = useCallback((client) => {
    setSelectedClient(client);
    setFormSteps(1);
    getData();
  }, []);

  const handleStepOne = async (values: { doc: string }) => {
    try {
      setClients([]);
      setLoadingBtn(true);

      const response = await api.get("getClientsAjustes", {
        dataDocument: values.doc.replace(/[./-]/g, "").replace(/\s/g, ""),
        document: currentDoc,
      });

      const hasCustomError = isCustomError(response);

      if (hasCustomError) {
        return notifyError(response.message, "", false, true);
      }

      const { data } = response;

      if (!data.length) {
        return notifyError("Cliente não encontrado", "", false, true);
      }

      setClients(data);
      if (data.length === 1) {
        onSelectClient(data[0]);
      }
    } catch (e) {
      const error = e as ErrorMessage;
      notifyError(error.message, "", false, true);
    } finally {
      setLoadingBtn(false);
    }
  };

  const handleSaveAdjustment = async (values: LaunchAdjustmentFormSchema) => {
    const {
      type,
      billingDate,
      value: fValue,
      activeInvoice,
      description,
    } = values;

    const typeLabel = typesAdjustments.find((item) => item.id === type);

    try {
      const numericValue = parseInt(fValue.replace(/[\D]+/g, ""), 10);
      const value = Number((numericValue / 100).toFixed(2));

      const response = await api.post("launch_adjustment", {
        idSettingLaunchType: type,
        clientId: selectedClient?.id,
        value,
        billingdate: billingDate,
        activeInvoice,
        description,
        launchTypeLabel: typeLabel?.descricao,
      });
      const hasCustomError = isCustomError(response);

      if (hasCustomError) {
        return notifyError(response.message, "", false, true);
      }
      notifySucces("Ajuste lançado com sucesso", true);
      handleResetSearch();
    } catch (e) {
      const error = e as ErrorMessage;
      notifyError(error.message, "", false, true);
    }
  };

  return {
    formSteps,
    currentDoc,
    loadingBtn,
    clients,
    loadingTypes,
    typesAdjustments,
    selectedClient,
    hasPermissionToUsePreviousDate,
    currentMask,
    handleTab,
    handleResetSearch,
    handleStepOne,
    handleSaveAdjustment,
    onSelectClient,
  };
}
